import { Delete, Edit } from '@mui/icons-material';
import {
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import CommonDialog from '../../../../common/CommonDialog';
import { activeTableSortLabel } from '../../../../config/MaterialUIConstants';
import { useDeleteSavedService } from '../../../../services/business/savedService';
import useSavedServiceStore from '../../../../store/useSavedServiceStore';
import { formatCurrency } from '../../../../utility/helpers/displayHelper';
import { formatVehicleMakeDisplay } from '../../../../utility/helpers/vehicleMakeHelper';
import VehicleMakesModal from './VehicleMakesModal';

const SavedServiceTable = ({ openModal, savedServiceList, savedServiceParams, setSavedServiceParams, savedServiceListPagination, refreshSavedServices }) => {
    const containerRef = useRef(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedServiceForDelete, setSelectedServiceForDelete] = useState(null);
    const [vehicleMakesModalOpen, setVehicleMakesModalOpen] = useState(false);
    const [selectedVehicleMakes, setSelectedVehicleMakes] = useState([]);

    const queryClient = useQueryClient();

    // TanStack Query delete hook
    const deleteSavedServiceMutation = useDeleteSavedService({
        onSuccess: (data) => {
            toast.success('Saved service deleted successfully');
            queryClient.removeQueries({ queryKey: ['savedService', 'infiniteList'], exact: false });
            refreshSavedServices();
            setDeleteModalOpen(false);
            setTimeout(() => {
                setSelectedServiceForDelete(null);
            }, 500);
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to delete saved service');
            console.error('Error deleting saved service:', error);
        },
    });

    // Transform API data to match table structure
    const dataToRender = useMemo(() => {
        if (!savedServiceList || savedServiceList.length === 0) {
            return [];
        }

        return savedServiceList.map((item) => {
            // Handle new array format for vehicle makes
            let vehicleMakesArray = [];
            if (Array.isArray(item.vehicleMake)) {
                vehicleMakesArray = item.vehicleMake;
            }

            return {
                _id: item._id,
                serviceName: item?.service?.name || '',
                vehicleMakes: vehicleMakesArray,
                vehicleMakeDisplay: vehicleMakesArray?.length === 0 ? 'All' : formatVehicleMakeDisplay(vehicleMakesArray),
                partsCount: item?.partsCount || 0,
                hours: item?.serviceTimeInHrs || '0:00',
                amount: item.totalAmount || 0,
                category: item?.serviceCategory?.name || '',
                pricingType: item.pricingType || 'N/A',
                labor: item?.labor?.qty || 0,
                parts: item?.parts || [],
                shopServiceReference: item?.shopServiceReference || '',
            };
        });
    }, [savedServiceList]);

    // Handle sorting functionality
    const handleSort = useCallback(
        (sortByField) => {
            const latestParams = useSavedServiceStore.getState().savedServiceParams;
            const isCurrentSort = latestParams.sortBy === sortByField;
            const newSortOrder = isCurrentSort && latestParams.sortOrder === 'asc' ? 'desc' : 'asc';

            setSavedServiceParams({
                ...latestParams,
                sortBy: sortByField,
                sortOrder: newSortOrder,
            });
        },
        [setSavedServiceParams]
    );

    const handleChangePage = useCallback(
        (event, newPage) => {
            const latestParams = useSavedServiceStore.getState().savedServiceParams;
            setSavedServiceParams({
                ...latestParams,
                page: newPage + 1,
            });
        },
        [setSavedServiceParams]
    );

    const handleChangeRowsPerPage = useCallback(
        (event) => {
            const latestParams = useSavedServiceStore.getState().savedServiceParams;
            setSavedServiceParams({
                ...latestParams,
                limit: event.target.value,
                page: 1,
            });
        },
        [setSavedServiceParams]
    );

    // Handle delete confirmation
    const handleDeleteClick = useCallback((service) => {
        setSelectedServiceForDelete(service);
        setDeleteModalOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (!selectedServiceForDelete?._id) return;

        deleteSavedServiceMutation.mutate(selectedServiceForDelete._id);
    }, [selectedServiceForDelete, deleteSavedServiceMutation]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteModalOpen(false);
        setSelectedServiceForDelete(null);
    }, []);

    // Handle vehicle makes modal
    const handleVehicleMakesClick = useCallback((vehicleMakes) => {
        if (vehicleMakes && vehicleMakes.length > 1) {
            setSelectedVehicleMakes(vehicleMakes);
            setVehicleMakesModalOpen(true);
        }
    }, []);

    const handleVehicleMakesModalClose = useCallback(() => {
        setVehicleMakesModalOpen(false);
        setSelectedVehicleMakes([]);
    }, []);

    // Individual row component
    const ServiceRow = React.memo(({ service }) => (
        <TableRow
            key={service._id}
            onDoubleClick={() => openModal(service._id)}
            sx={{
                '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '& .MuiTableCell-root': {
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    // fontSize: '16px',
                },
                cursor: 'pointer',
            }}
        >
            <TableCell sx={{}}>{service.serviceName}</TableCell>
            <TableCell>{service.shopServiceReference}</TableCell>
            <TableCell>{service.category}</TableCell>
            <TableCell sx={{}}>
                <Typography
                    variant="body2"
                    sx={{
                        cursor: service.vehicleMakes.length > 1 ? 'pointer' : 'default',
                        textDecoration: service.vehicleMakes.length > 1 ? 'underline' : 'none',
                    }}
                    onClick={() => handleVehicleMakesClick(service.vehicleMakes)}
                >
                    {service.vehicleMakeDisplay}
                </Typography>
            </TableCell>
            {/* <TableCell sx={{}}>{service.partsCount}</TableCell>
            <TableCell sx={{}}>{service.labor}</TableCell> */}
            <TableCell sx={{}}>{formatCurrency(service.amount)}</TableCell>
            <TableCell>
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <IconButton
                        size="small"
                        onClick={() => openModal(service._id)}
                        sx={{
                            color: 'rgba(0, 0, 0, 0.56)',
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            },
                        }}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(service)}
                        sx={{
                            color: 'error.main',
                            '&:hover': {
                                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                            },
                        }}
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                </Stack>
            </TableCell>
        </TableRow>
    ));

    ServiceRow.displayName = 'ServiceRow';

    return (
        <TableContainer
            ref={containerRef}
            sx={{
                mt: 2,
                height: '100%',
                maxHeight: '480px',
                backgroundColor: 'transparent',
                overflow: 'auto',
                width: '100%',
            }}
        >
            <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: '300px', fontSize: '16px' }}>
                            <TableSortLabel
                                // active={savedServiceParams.sortBy === 'serviceName'}
                                active={true}
                                direction={savedServiceParams.sortBy === 'serviceName' ? savedServiceParams.sortOrder.toLowerCase() : 'dsc'}
                                onClick={() => handleSort('serviceName')}
                                sx={savedServiceParams.sortBy === 'serviceName' && activeTableSortLabel}
                            >
                                Service Name
                            </TableSortLabel>
                        </TableCell>

                        <TableCell sx={{ width: '200px', fontSize: '16px' }}>
                            {' '}
                            <TableSortLabel
                                active={true}
                                direction={savedServiceParams.sortBy === 'shopServiceReference' ? savedServiceParams.sortOrder.toLowerCase() : 'dsc'}
                                onClick={() => handleSort('shopServiceReference')}
                                sx={savedServiceParams.sortBy === 'shopServiceReference' && activeTableSortLabel}
                            >
                                Shop Reference
                            </TableSortLabel>
                        </TableCell>

                        <TableCell sx={{ width: '200px', fontSize: '16px' }}>Category</TableCell>

                        <TableCell sx={{ width: '150px', fontSize: '16px' }}>Vehicle Make</TableCell>

                        {/* Sortable Parts Column */}
                        {/* <TableCell sx={{ width: '140px', fontSize: '16px' }}>
                            <TableSortLabel
                                // active={savedServiceParams.sortBy === 'partsCount'}
                                active={true}
                                direction={savedServiceParams.sortBy === 'partsCount' ? savedServiceParams.sortOrder.toLowerCase() : 'dsc'}
                                onClick={() => handleSort('partsCount')}
                                sx={savedServiceParams.sortBy === 'partsCount' && activeTableSortLabel}
                            >
                                Parts
                            </TableSortLabel>
                        </TableCell> */}

                        {/* Sortable Hours Column */}
                        {/* <TableCell sx={{ width: '140px', fontSize: '16px' }}>
                            <TableSortLabel
                                // active={savedServiceParams.sortBy === 'labor.qty'}
                                active={true}
                                direction={savedServiceParams.sortBy === 'labor.qty' ? savedServiceParams.sortOrder.toLowerCase() : 'dsc'}
                                onClick={() => handleSort('labor.qty')}
                                sx={savedServiceParams.sortBy === 'labor.qty' && activeTableSortLabel}
                            >
                                Hours
                            </TableSortLabel>
                        </TableCell> */}

                        {/* Sortable Amount Column */}
                        <TableCell sx={{ width: '140px', fontSize: '16px' }}>
                            <TableSortLabel
                                // active={savedServiceParams.sortBy === 'totalAmount'}
                                active={true}
                                direction={savedServiceParams.sortBy === 'totalAmount' ? savedServiceParams.sortOrder.toLowerCase() : 'dsc'}
                                onClick={() => handleSort('totalAmount')}
                                sx={savedServiceParams.sortBy === 'totalAmount' && activeTableSortLabel}
                            >
                                Price
                            </TableSortLabel>
                        </TableCell>

                        <TableCell sx={{ width: '80px', fontSize: '16px' }}>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* Show Empty State if no records */}
                    {dataToRender.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={6}
                                align="center"
                                sx={{
                                    height: '422px',
                                    border: 'none',
                                    fontSize: '16px',
                                    color: 'rgba(0,0,0,0.6)',
                                }}
                            >
                                No records found
                            </TableCell>
                        </TableRow>
                    )}

                    {/* Render visible rows */}
                    {dataToRender.map((service) => (
                        <ServiceRow key={service._id} service={service} />
                    ))}
                </TableBody>
            </Table>
            <Grid container item alignItems={'center'} justifyContent="end">
                <Grid item>
                    <TablePagination
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        colSpan={3}
                        count={savedServiceListPagination?.totalCount}
                        rowsPerPage={savedServiceParams?.limit}
                        page={savedServiceParams?.page - 1}
                        slotProps={{
                            select: {
                                'aria-label': 'rows per page',
                            },
                            actions: {
                                showFirstButton: true,
                                showLastButton: true,
                            },
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Grid>
            </Grid>

            {/* Delete Confirmation Modal */}
            <CommonDialog
                open={deleteModalOpen}
                onClose={handleDeleteCancel}
                onSave={handleDeleteConfirm}
                loader={deleteSavedServiceMutation.isPending}
                dialogTitle="Delete Saved Service?"
                dialogSubtitle={`Are you sure you want to delete the service ${selectedServiceForDelete?.serviceName}?`}
                // dialogSubtitle2={
                //     selectedServiceForDelete
                //         ? `Service Details:\nVehicle Make: ${selectedServiceForDelete.vehicleMake}\nCategory: ${selectedServiceForDelete.category}\nParts: ${
                //               selectedServiceForDelete.partsCount
                //           }\nAmount: ${formatCurrency(selectedServiceForDelete.amount)}`
                //         : ''
                // }
                saveButtonText="Delete"
                saveButtonType="error"
                cancelButtonText="Cancel"
            />

            {/* Vehicle Makes Modal */}
            <VehicleMakesModal open={vehicleMakesModalOpen} onClose={handleVehicleMakesModalClose} vehicleMakes={selectedVehicleMakes} />
        </TableContainer>
    );
};

export default React.memo(SavedServiceTable);
